import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { computeCompleteness } from '../hooks/useProfileCompleteness';
import { useProfiles } from '../queries';

export const SIDEBAR_WIDTH = 220;

const NAV_ITEMS = [
  {
    to: '/dashboard',
    label: 'My Tenders',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
        <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
        <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
        <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
      </svg>
    ),
  },
  {
    to: '/profiles',
    label: 'My profiles',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="5" r="3" fill="currentColor"/>
        <path d="M2 13c0-3.3 2.7-6 6-6s6 2.7 6 6H2z" fill="currentColor"/>
      </svg>
    ),
  },
  {
    to: '/profile',
    label: 'Company',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="6" width="14" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M5 15V10h6v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 6V4a4 4 0 0 1 8 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
  },
  {
    to: '/search',
    label: 'Browse tenders',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="6.5" cy="6.5" r="4" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M9.5 9.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="2" fill="currentColor"/>
        <path fillRule="evenodd" clipRule="evenodd"
          d="M8 1a1 1 0 0 1 .97.757l.3 1.19a5.2 5.2 0 0 1 1.06.617l1.17-.39a1 1 0 0 1 1.15.46l1 1.732a1 1 0 0 1-.23 1.28l-.94.75a5.3 5.3 0 0 1 0 1.212l.94.75a1 1 0 0 1 .23 1.28l-1 1.732a1 1 0 0 1-1.15.46l-1.17-.39a5.2 5.2 0 0 1-1.06.617l-.3 1.19A1 1 0 0 1 8 15a1 1 0 0 1-.97-.757l-.3-1.19a5.2 5.2 0 0 1-1.06-.617l-1.17.39a1 1 0 0 1-1.15-.46l-1-1.732a1 1 0 0 1 .23-1.28l.94-.75a5.3 5.3 0 0 1 0-1.212l-.94-.75a1 1 0 0 1-.23-1.28l1-1.732a1 1 0 0 1 1.15-.46l1.17.39a5.2 5.2 0 0 1 1.06-.617l.3-1.19A1 1 0 0 1 8 1z"
          fill="currentColor" opacity="0.5"/>
      </svg>
    ),
  },
  {
    to: '/billing',
    label: 'Billing',
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M1 6.5h14" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
  },
];

const s = {
  sidebar: {
    position: 'fixed',
    top: 60,
    left: 0,
    bottom: 0,
    width: SIDEBAR_WIDTH,
    background: '#0A1F4A',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 200,
    borderRight: '1px solid rgba(255,255,255,0.08)',
  },
  section: {
    padding: '20px 12px 8px',
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    padding: '0 8px',
    marginBottom: 4,
    fontFamily: '"JetBrains Mono", monospace',
  },
  item: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? '#ECEEF3' : 'rgba(255,255,255,0.55)',
    background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    marginBottom: 2,
    transition: 'background 0.15s, color 0.15s',
  }),
  iconWrap: (active) => ({
    color: active ? '#FF7A3D' : 'rgba(255,255,255,0.35)',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  }),
  divider: {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    margin: '8px 12px',
  },
};

export default function Sidebar({ topOffset = 60 }) {
  const { pathname } = useLocation();
  // Shares the cached /profiles fetch with the rest of the app.
  const { data: profiles } = useProfiles();
  let profileScore = null;
  let defaultProfile = null;
  if (profiles && profiles.length > 0) {
    defaultProfile = profiles.find((p) => p.is_default) || profiles[0];
    profileScore = computeCompleteness(defaultProfile).score;
  }

  // Setup checklist (data-audit fix 2b): the few unmet items that measurably
  // move matching. Only shown once a profile exists (mid-onboarding users get
  // no nag) and disappears entirely when complete. (Working languages and
  // certifications left the checklist 2026-07-19 — both are out of matching.)
  const setupItems = [];
  if (defaultProfile) {
    const missing = computeCompleteness(defaultProfile).missing.map((m) => m.key);
    if (missing.includes('capabilities_description')) {
      setupItems.push({ to: `/profiles/${defaultProfile.id}/edit`, label: 'Describe your capabilities' });
    }
    if (missing.includes('reference_projects')) {
      setupItems.push({ to: `/profiles/${defaultProfile.id}/edit`, label: 'Add reference projects' });
    }
  }

  return (
    <aside style={{ ...s.sidebar, top: topOffset }}>
      <div style={s.section}>
        <div style={s.sectionLabel}>Application</div>
        {NAV_ITEMS.map((item) => {
          const active = pathname === item.to || pathname.startsWith(item.to + '/');
          return (
            <Link key={item.to} to={item.to} style={s.item(active)}>
              <span style={s.iconWrap(active)}>{item.icon}</span>
              {item.label}
              {item.to === '/profiles' && profileScore !== null && profileScore < 80 && (
                <span style={{
                  marginLeft: 'auto', width: 7, height: 7,
                  borderRadius: '50%', background: '#FF7A3D', flexShrink: 0,
                }} />
              )}
            </Link>
          );
        })}
      </div>

      {setupItems.length > 0 && (
        <>
          <div style={s.divider} />
          <div style={s.section}>
            <div style={s.sectionLabel}>Finish setup</div>
            {setupItems.map((item) => (
              <Link key={item.label} to={item.to} style={{ ...s.item(false), fontSize: 13 }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: '#FF7A3D', flexShrink: 0,
                }} />
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}

    </aside>
  );
}
